import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
  Chip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Visibility as ViewsIcon,
  Favorite as LikesIcon,
  Comment as CommentsIcon,
  Share as SharesIcon,
  TouchApp as ClicksIcon,
  AttachMoney as RevenueIcon,
  Person as FollowersIcon,
  Public as ReachIcon,
  Speed as EngagementIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon,
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { analyticsApi, trackAnalyticsEvent } from '../services/analyticsApi';
import { useDemoMode } from '../providers/DemoModeProvider';
import { useAuth } from '../providers/AuthProvider';
import DateRangeSelector from '../components/analytics/DateRangeSelector';
import PerformanceTimelineChart from '../components/analytics/PerformanceTimelineChart';
import PlatformComparisonChart from '../components/analytics/PlatformComparisonChart';
import TopPerformingPostsTable from '../components/analytics/TopPerformingPostsTable';
import AnalyticsOverviewCards from '../components/analytics/AnalyticsOverviewCards';
import AnalyticsErrorBoundary from '../components/analytics/AnalyticsErrorBoundary';

// Demo/Mock data for development and testing
const MOCK_ANALYTICS_DATA = {
  overview: {
    totalPosts: 156,
    totalImpressions: 1250000,
    totalEngagement: 89500,
    totalClicks: 42300,
    totalReach: 890000,
    totalFollowers: 15680,
    avgEngagementRate: 7.2,
    totalRevenue: 245600,
    conversions: 1240
  },
  previousPeriod: {
    totalPosts: 134,
    totalImpressions: 980000,
    totalEngagement: 72100,
    totalClicks: 35800,
    totalReach: 720000,
    totalFollowers: 14200,
    avgEngagementRate: 6.8,
    totalRevenue: 189300,
    conversions: 980
  },
  performanceTimeline: [
    { date: '2024-11-01', impressions: 42000, engagement: 3200, clicks: 1580, reach: 35000 },
    { date: '2024-11-02', impressions: 38000, engagement: 2890, clicks: 1420, reach: 32000 },
    { date: '2024-11-03', impressions: 45000, engagement: 3450, clicks: 1680, reach: 38000 },
    { date: '2024-11-04', impressions: 52000, engagement: 3920, clicks: 1890, reach: 43000 },
    { date: '2024-11-05', impressions: 48000, engagement: 3650, clicks: 1750, reach: 40000 },
    { date: '2024-11-06', impressions: 55000, engagement: 4180, clicks: 2020, reach: 46000 },
    { date: '2024-11-07', impressions: 60000, engagement: 4580, clicks: 2200, reach: 50000 }
  ],
  platformBreakdown: [
    {
      platform: 'instagram',
      posts: 42,
      impressions: 380000,
      engagement: 28500,
      engagementRate: 7.5,
      clicks: 12800,
      ctr: 3.37,
      revenue: 78500,
      followers: 5200
    },
    {
      platform: 'twitter',
      posts: 38,
      impressions: 290000,
      engagement: 21200,
      engagementRate: 7.3,
      clicks: 9800,
      ctr: 3.38,
      revenue: 58900,
      followers: 4100
    },
    {
      platform: 'linkedin',
      posts: 28,
      impressions: 320000,
      engagement: 24500,
      engagementRate: 7.7,
      clicks: 11200,
      ctr: 3.5,
      revenue: 68200,
      followers: 3800
    },
    {
      platform: 'facebook',
      posts: 32,
      impressions: 180000,
      engagement: 12800,
      engagementRate: 7.1,
      clicks: 6500,
      ctr: 3.61,
      revenue: 28000,
      followers: 1900
    },
    {
      platform: 'tiktok',
      posts: 16,
      impressions: 80000,
      engagement: 2500,
      engagementRate: 3.1,
      clicks: 2000,
      ctr: 2.5,
      revenue: 12000,
      followers: 680
    }
  ],
  topPosts: [
    {
      id: 1,
      content: "Excited to announce our latest product update! 🚀 New automation features that will save you hours every week. Check out the demo in our bio!",
      platform: 'linkedin',
      impressions: 28500,
      engagement: 2850,
      engagementRate: 10.0,
      clicks: 1200,
      publishedAt: '2024-11-05T14:00:00Z'
    },
    {
      id: 2,
      content: "Behind the scenes: How our team built the most requested feature in just 2 sprints 💪 Thread below 👇",
      platform: 'twitter',
      impressions: 22100,
      engagement: 2010,
      engagementRate: 9.1,
      clicks: 890,
      publishedAt: '2024-11-03T16:30:00Z'
    },
    {
      id: 3,
      content: "Customer spotlight 🌟 How @TechStartup increased their team productivity by 250% using our platform",
      platform: 'instagram',
      impressions: 19800,
      engagement: 1680,
      engagementRate: 8.5,
      clicks: 720,
      publishedAt: '2024-11-02T11:15:00Z'
    }
  ]
};

/**
 * Main Analytics Dashboard Page Component
 * 
 * Provides comprehensive analytics overview across all user's social media platforms
 * Features:
 * - Overview metrics with comparison to previous period
 * - Performance timeline chart
 * - Platform-specific breakdown and comparison
 * - Top performing posts analysis
 * - Date range filtering
 * - Data export functionality
 * - Real-time data updates
 */
const AnalyticsDashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isDemoMode } = useDemoMode();
  const { user } = useAuth();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [customDateStart, setCustomDateStart] = useState(dayjs().subtract(30, 'day'));
  const [customDateEnd, setCustomDateEnd] = useState(dayjs());
  const [platformFilter, setPlatformFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('loading');

  /**
   * Load analytics data from API with error handling and fallback
   */
  const loadAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Track analytics dashboard view
      trackAnalyticsEvent('analytics_dashboard_viewed', {
        dateRange,
        platformFilter,
        isDemo: isDemoMode
      });

      // Check if demo mode is enabled
      if (isDemoMode) {
        console.log('🎬 Demo mode enabled, using mock analytics data');
        setAnalyticsData(MOCK_ANALYTICS_DATA);
        setDataSource('demo');
        setLoading(false);
        return;
      }

      // Prepare API parameters
      const apiParams = {
        timeRange: dateRange,
        platform: platformFilter !== 'all' ? platformFilter : undefined
      };

      // If custom date range, add date parameters
      if (dateRange === 'custom') {
        apiParams.startDate = customDateStart.format('YYYY-MM-DD');
        apiParams.endDate = customDateEnd.format('YYYY-MM-DD');
      }

      // Load real analytics data from backend
      try {
        const response = await analyticsApi.getDashboard();
        
        if (response && response.overview) {
          setAnalyticsData(response);
          setDataSource('real');
          console.log('✅ Loaded real analytics data:', response);
        } else {
          throw new Error('Invalid analytics response format');
        }
      } catch (analyticsError) {
        console.warn('⚠️ Analytics API failed, falling back to mock data:', analyticsError.message);
        // Fallback to mock data for better user experience
        setAnalyticsData(MOCK_ANALYTICS_DATA);
        setDataSource('mock');
        setError('Using sample data - analytics service is being configured');
      }

    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError('Failed to load analytics data');
      // Use mock data as final fallback
      setAnalyticsData(MOCK_ANALYTICS_DATA);
      setDataSource('mock');
    } finally {
      setLoading(false);
    }
  }, [dateRange, platformFilter, customDateStart, customDateEnd, isDemoMode]);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  /**
   * Handle manual data refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    trackAnalyticsEvent('analytics_refresh_clicked', {
      dateRange,
      platformFilter
    });
    
    try {
      await loadAnalyticsData();
    } finally {
      setRefreshing(false);
    }
  }, [loadAnalyticsData, dateRange, platformFilter]);

  /**
   * Handle date range change
   */
  const handleDateRangeChange = useCallback((newRange) => {
    setDateRange(newRange);
    trackAnalyticsEvent('analytics_date_range_changed', {
      newRange,
      platformFilter
    });
  }, [platformFilter]);

  /**
   * Handle platform filter change
   */
  const handlePlatformFilterChange = useCallback((newPlatform) => {
    setPlatformFilter(newPlatform);
    trackAnalyticsEvent('analytics_platform_filter_changed', {
      newPlatform,
      dateRange
    });
  }, [dateRange]);

  /**
   * Handle data export
   */
  const handleExport = useCallback((format) => {
    trackAnalyticsEvent('analytics_export_requested', {
      format,
      dateRange,
      platformFilter
    });
    
    // Implementation will be added in the export utility component
    console.log('Export requested:', { format, dateRange, platformFilter });
  }, [dateRange, platformFilter]);

  // Memoized overview metrics for performance
  const overviewMetrics = useMemo(() => {
    if (!analyticsData?.overview) return [];

    const { overview, previousPeriod } = analyticsData;
    
    return [
      {
        title: 'Total Impressions',
        value: overview.totalImpressions,
        previousValue: previousPeriod?.totalImpressions,
        icon: <ViewsIcon />,
        color: 'primary'
      },
      {
        title: 'Total Engagement',
        value: overview.totalEngagement,
        previousValue: previousPeriod?.totalEngagement,
        icon: <EngagementIcon />,
        color: 'success'
      },
      {
        title: 'Engagement Rate',
        value: `${overview.avgEngagementRate}%`,
        previousValue: previousPeriod?.avgEngagementRate,
        icon: <TrendingUpIcon />,
        color: 'warning'
      },
      {
        title: 'Total Reach',
        value: overview.totalReach,
        previousValue: previousPeriod?.totalReach,
        icon: <ReachIcon />,
        color: 'info'
      },
      {
        title: 'Total Clicks',
        value: overview.totalClicks,
        previousValue: previousPeriod?.totalClicks,
        icon: <ClicksIcon />,
        color: 'secondary'
      },
      {
        title: 'Total Revenue',
        value: `$${overview.totalRevenue?.toLocaleString()}`,
        previousValue: previousPeriod?.totalRevenue,
        icon: <RevenueIcon />,
        color: 'error'
      }
    ];
  }, [analyticsData]);

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '60vh', justifyContent: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading analytics dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <AnalyticsErrorBoundary>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DashboardIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4" component="h1">
                  Analytics Dashboard
                </Typography>
                {(isDemoMode || dataSource === 'demo') && (
                  <Chip 
                    label="DEMO" 
                    size="small" 
                    color="primary" 
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                  />
                )}
              </Box>
              <Typography variant="subtitle1" color="text.secondary">
                Comprehensive performance insights across all your social media platforms
              </Typography>
              {dataSource === 'demo' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  🎬 Demo Mode: Exploring analytics features with sample data
                </Typography>
              )}
              {dataSource === 'mock' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  📊 Sample Data: Analytics backend is being configured
                </Typography>
              )}
            </Box>
            
            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <DateRangeSelector
                value={dateRange}
                onChange={handleDateRangeChange}
                customStartDate={customDateStart}
                customEndDate={customDateEnd}
                onCustomStartDateChange={setCustomDateStart}
                onCustomEndDateChange={setCustomDateEnd}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={platformFilter}
                  onChange={(e) => handlePlatformFilterChange(e.target.value)}
                  label="Platform"
                >
                  <MenuItem value="all">All Platforms</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="twitter">Twitter</MenuItem>
                  <MenuItem value="linkedin">LinkedIn</MenuItem>
                  <MenuItem value="facebook">Facebook</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                </Select>
              </FormControl>
              
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  color="primary"
                >
                  {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('csv')}
                size="small"
              >
                Export
              </Button>
            </Box>
          </Box>

          {/* Status Alerts */}
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/*{dataSource === 'real' && (*/}
          {/*  <Alert severity="success" sx={{ mb: 2 }}>*/}
          {/*    ✅ Live Data: Showing real analytics from your social media accounts*/}
          {/*  </Alert>*/}
          {/*)}*/}
        </Box>

        {/* Overview Metrics Cards */}
        <AnalyticsOverviewCards metrics={overviewMetrics} />

        <Divider sx={{ my: 4 }} />

        {/* Main Analytics Content */}
        <Grid container spacing={3}>
          {/* Performance Timeline */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon />
                  Performance Timeline
                </Typography>
                <PerformanceTimelineChart 
                  data={analyticsData?.performanceTimeline || []}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Platform Comparison */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Platform Comparison
                </Typography>
                <PlatformComparisonChart 
                  data={analyticsData?.platformBreakdown || []}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Top Performing Posts */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Performing Posts
                </Typography>
                <TopPerformingPostsTable 
                  data={analyticsData?.topPosts || []}
                  loading={loading}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </AnalyticsErrorBoundary>
  );
};

export default AnalyticsDashboardPage;